import { useEffect, useState } from "react";
import { securityMonitoringApi } from "../../services/securityMonitoringApi";
import { Card } from "../../components/common/Card";
import { Tabs } from "../../components/common/Tabs";
import { GapNotice } from "../../components/common/GapNotice";
import { AdminActionButton } from "../../components/common/AdminActionButton";
import { DataTable } from "../../components/common/DataTable";
import { StatTile } from "../../components/common/StatTile";

const SUSPICIOUS_THRESHOLD = 5;

/**
 * Failed logins, geofence violations, device fingerprinting and suspicious
 * activity are now backed by audit.SecurityEventLog (see
 * securityMonitoringApi.js's header comment) and wired to real data below.
 * Anomaly detection, risk scoring, session replay and threat intel have no
 * backend support yet and stay as honest GapNotices.
 */
export function SecurityMonitoringPage() {
  const [runbooks] = useState(securityMonitoringApi.runbooks());
  const [failedLogins, setFailedLogins] = useState(null);
  const [geofence, setGeofence] = useState(null);
  const [devices, setDevices] = useState(null);
  const [suspicious, setSuspicious] = useState(null);

  useEffect(() => {
    securityMonitoringApi.failedLogins({ limit: 50, offset: 0 }).then(setFailedLogins).catch(() => setFailedLogins({ recent: [], by_email_24h: [] }));
    securityMonitoringApi.geofenceViolations({ limit: 50, offset: 0 }).then(setGeofence).catch(() => setGeofence({ violations: [] }));
    securityMonitoringApi.deviceFingerprints().then(setDevices).catch(() => setDevices({ trusted_devices: [], unrecognized_device_events: [] }));
    securityMonitoringApi.suspiciousActivity(SUSPICIOUS_THRESHOLD).then(setSuspicious).catch(() => setSuspicious({ suspicious_activity: [], threshold: SUSPICIOUS_THRESHOLD, window_hours: 24 }));
  }, []);

  const tabs = [
    {
      key: "anomalies",
      label: "Anomaly Detection",
      content: (
        <GapNotice>
          Needs a scoring model (rules-based or ML) analyzing login time-of-day, device changes, and location deltas
          per session, writing findings somewhere queryable. No such model or table exists yet.
        </GapNotice>
      ),
    },
    {
      key: "risk",
      label: "Risk Scoring",
      content: (
        <GapNotice>
          app.UserSessions.risk_score (0-100, CHECK constraint) already exists in the schema and is read by
          api/functions/account.py's session list -- nothing ever calculates a real value into it, so every session
          shows 0 today. A breakdown ("+20 new device, +15 odd hour, ...") needs a scoring job storing its factors
          somewhere, not just the single column.
        </GapNotice>
      ),
    },
    {
      key: "suspicious",
      label: "Suspicious Activity",
      content: (
        <div>
          <div className="section-spacing" style={{ display: "flex", gap: "var(--space-3)", flexWrap: "wrap" }}>
            <StatTile label="Detection Threshold" value={suspicious ? suspicious.threshold : SUSPICIOUS_THRESHOLD} sublabel={`events in ${suspicious ? suspicious.window_hours : 24}h window`} />
            <StatTile label="Flagged Identifiers" value={suspicious ? suspicious.suspicious_activity.length : 0} />
          </div>
          <Card title="Suspicious Activity (repeated events past threshold)">
            <DataTable
              loading={!suspicious}
              rows={suspicious?.suspicious_activity.map((r, i) => ({ ...r, _key: `${r.identifier}-${r.event_type}-${i}` }))}
              rowKey="_key"
              emptyTitle="No suspicious activity detected"
              emptyDescription={`Nothing has crossed ${suspicious?.threshold ?? SUSPICIOUS_THRESHOLD} repeated events in the last ${suspicious?.window_hours ?? 24}h.`}
              columns={[
                { key: "identifier", header: "Identifier", render: (r) => r.identifier },
                { key: "ip_address", header: "IP Address" },
                { key: "event_type", header: "Event Type" },
                { key: "event_count", header: "Event Count" },
                { key: "last_event", header: "Last Event", render: (r) => new Date(r.last_event).toLocaleString() },
              ]}
            />
          </Card>
        </div>
      ),
    },
    {
      key: "geofence",
      label: "Geofencing Violations",
      content: (
        <Card title="Geofence Violations">
          <DataTable
            loading={!geofence}
            rows={geofence?.violations}
            rowKey="event_id"
            emptyTitle="No geofence violations recorded"
            columns={[
              { key: "attempted_email", header: "Email" },
              { key: "ip_address", header: "IP Address" },
              { key: "details", header: "Details" },
              { key: "created_date", header: "When", render: (r) => new Date(r.created_date).toLocaleString() },
            ]}
          />
        </Card>
      ),
    },
    {
      key: "devices",
      label: "Device Fingerprinting",
      content: (
        <div>
          <Card title="Trusted Devices" className="section-spacing">
            <DataTable
              loading={!devices}
              rows={devices?.trusted_devices}
              rowKey="device_fingerprint"
              emptyTitle="No trusted devices recorded"
              columns={[
                { key: "device_fingerprint", header: "Device Fingerprint" },
                { key: "user_count", header: "User Count" },
                { key: "last_used", header: "Last Used", render: (r) => new Date(r.last_used).toLocaleString() },
              ]}
            />
          </Card>
          <Card title="Unrecognized Device Events">
            <DataTable
              loading={!devices}
              rows={devices?.unrecognized_device_events}
              rowKey="device_fingerprint"
              emptyTitle="No unrecognized device events recorded"
              columns={[
                { key: "device_fingerprint", header: "Device Fingerprint" },
                { key: "event_count", header: "Event Count" },
                { key: "last_seen", header: "Last Seen", render: (r) => new Date(r.last_seen).toLocaleString() },
              ]}
            />
          </Card>
        </div>
      ),
    },
    {
      key: "failed-logins",
      label: "Failed Login Attempts",
      content: (
        <div>
          <Card title="Recent Failed Login Attempts" className="section-spacing">
            <DataTable
              loading={!failedLogins}
              rows={failedLogins?.recent}
              rowKey="event_id"
              emptyTitle="No failed login attempts recorded"
              columns={[
                { key: "attempted_email", header: "Email" },
                { key: "ip_address", header: "IP Address" },
                { key: "device_fingerprint", header: "Device Fingerprint" },
                { key: "details", header: "Details" },
                { key: "created_date", header: "When", render: (r) => new Date(r.created_date).toLocaleString() },
              ]}
            />
          </Card>
          <Card title="Repeated Attempts by Email (past 24h)">
            <DataTable
              loading={!failedLogins}
              rows={failedLogins?.by_email_24h}
              rowKey="attempted_email"
              emptyTitle="No email has repeated failed attempts in the past 24h"
              columns={[
                { key: "attempted_email", header: "Email" },
                { key: "attempt_count", header: "Attempts" },
                { key: "last_attempt", header: "Last Attempt", render: (r) => new Date(r.last_attempt).toLocaleString() },
              ]}
            />
          </Card>
        </div>
      ),
    },
    {
      key: "replay",
      label: "Session Replay",
      content: <GapNotice>Needs a client-side session-recording library (e.g. rrweb) on the consumer apps shipping redacted events to a new endpoint -- a real privacy/legal review is warranted given the explicit PII-redaction requirement, not just an engineering task.</GapNotice>,
    },
    {
      key: "threat-intel",
      label: "Threat Intelligence",
      content: <GapNotice>Needs an actual Microsoft Sentinel workspace + Graph Security API integration -- nothing to build here without that infrastructure existing first.</GapNotice>,
    },
    {
      key: "runbooks",
      label: "Incident Response",
      content: (
        <div style={{ display: "grid", gap: "var(--space-4)" }}>
          {runbooks.map((rb) => (
            <Card key={rb.id} title={rb.title}>
              <ol>
                {rb.steps.map((step) => <li key={step}>{step}</li>)}
              </ol>
              <div style={{ display: "flex", gap: "var(--space-2)", marginTop: "var(--space-3)" }}>
                <AdminActionButton
                  label="Force Logout All Sessions"
                  confirmBody={<p>This runbook step needs a target user ID -- wire this up from a specific user's detail page in User Management once /management/users/{"{id}"}/force-logout exists.</p>}
                  onConfirm={async () => {}}
                />
              </div>
            </Card>
          ))}
        </div>
      ),
    },
  ];

  return (
    <div>
      <h1>Security Monitoring</h1>
      <Card>
        <Tabs tabs={tabs} />
      </Card>
    </div>
  );
}
